/**
 * Page chassis ported from kyc-app's SimpleAppShell: glass top ribbon with the
 * brand wordmark and an EN/ไทย toggle, over the gradient/aurora page background
 * (all painted by `.shell` in theme.css). Screens render inside as a centered
 * mobile-first column (`.screen`).
 */
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { rememberLanguage } from '../i18n/languageChoice';
import { BrandMark } from './BrandMark';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'th', label: 'ไทย' },
] as const;

function LangToggle() {
  // `i18n` is optional-chained throughout: component tests mock react-i18next
  // with a bare `{ t }`, and the toggle must stay inert there, not crash.
  const { i18n } = useTranslation();
  const current = i18n?.language ?? 'en';

  // Keep <html lang> in sync for screen readers and for line-breaking: Sarabun
  // carries both scripts, so the font no longer switches with the language, but
  // Thai word-breaking still depends on the declared lang.
  useEffect(() => {
    document.documentElement.lang = current;
  }, [current]);

  return (
    <div className="lang-toggle" role="group" aria-label="Language">
      {LANGUAGES.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          aria-pressed={current.startsWith(code)}
          data-track={`language_${code}`}
          onClick={() => {
            rememberLanguage(code);
            void i18n?.changeLanguage(code);
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="shell">
      <header className="shell-header">
        <a className="brand" href="/" aria-label="trustsy">
          <BrandMark size={28} className="brand-mark" />
          <span className="brand-wordmark">trustsy</span>
        </a>
        <LangToggle />
      </header>
      {children}
    </div>
  );
}
